import React, { useState, useRef } from 'react';

/**
 * SUIVI DE STAGE — l'éditeur de la fiche de liaison (lot 3.8c)
 * =============================================================
 * Maquette : #formModeleFiche — encadré « structure fixe », deux colonnes
 * (contenu configurable | sections optionnelles), la section Missions avec sa
 * bascule et son « Configurer » (libellé, aide), une ligne par section (poignée,
 * titre, résumé, bascule, configurer, corbeille), le formulaire « Nouvelle
 * section », le bandeau « Ordre des sections dans le formulaire envoyé ».
 *
 * ── CE QUI S'AJOUTE À LA MAQUETTE ──────────────────────────────────────────
 *   · « Qui remplit ? » (étudiant / maître / les deux) et les OPTIONS d'une liste,
 *     d'un choix unique ou de cases à cocher (E114) ;
 *   · la section 🏛️ « Convention université » VERROUILLÉE (E54, E114) : le tableau
 *     de bord la lit par son code. Elle est à sa place réelle, la dernière — c'est
 *     là que la page du maître l'affiche, dans son propre encadré ;
 *   · ⚠️ une section DÉJÀ RÉPONDUE ne se supprime pas (E113) : la supprimer
 *     effacerait les réponses en cascade. Sa corbeille disparaît ; la bascule la
 *     désactive (elle quitte le formulaire des prochaines campagnes) ;
 *   · le bandeau d'E101 quand une campagne a figé sa fiche (E115).
 */

const CODE_CONVENTION = 'convention_universite';

const TYPES = [
    { v: 'textarea', l: 'Texte long (zone de saisie)', court: 'Texte long' },
    { v: 'text', l: 'Texte court (une ligne)', court: 'Texte court' },
    { v: 'select', l: 'Liste déroulante', court: 'Liste déroulante' },
    { v: 'radio', l: 'Choix unique (radio)', court: 'Choix unique' },
    { v: 'checkbox', l: 'Cases à cocher', court: 'Cases à cocher' },
    { v: 'date', l: 'Date', court: 'Date' }
];
const QUI = [
    { v: 'etudiant', l: "L'étudiant", court: "rempli par l'étudiant" },
    { v: 'maitre', l: 'Le maître de stage', court: 'rempli par le maître de stage' },
    { v: 'les_deux', l: "Les deux (l'étudiant, puis le maître corrige)", court: 'rempli par les deux' }
];
const AVEC_OPTIONS = ['select', 'radio', 'checkbox'];

let compteur = 0;
const cle = () => `s${++compteur}`;

/**
 * La section 🏛️ d'un modèle neuf. 2026-10-21 (Bertrand : « un paramétrage comme un autre ») : une section COMME LES
 * AUTRES — elle se configure, se déplace, se désactive, se supprime ; « Remettre la case » la rajoute. Son code reste
 * sur sa ligne : la colonne 🏛️ du tableau de bord la lit (sans elle, la colonne dit « — »).
 */
const SECTION_CONVENTION = {
    id: null, code: CODE_CONVENTION, actif: true,
    titre: "Sauf contre-ordre, la convention sera celle de l'université",
    type_champ: 'checkbox', remplissage: 'maitre', obligatoire: false,
    placeholder: 'Décochez si votre entreprise impose sa propre convention de stage.', options: []
};

const resume = (s) => [
    (TYPES.find((t) => t.v === s.type_champ) || {}).court,
    (QUI.find((q) => q.v === s.remplissage) || {}).court,
    s.obligatoire ? 'obligatoire' : 'optionnelle'
].filter(Boolean).join(' · ');

/** Les options s'éditent une par ligne ; la base les garde en liste. */
const versTexte = (options) => (Array.isArray(options) ? options : []).join('\n');
const versListe = (texte) => String(texte || '').split('\n').map((x) => x.trim()).filter(Boolean);

/** Les champs d'une section — pour la « Nouvelle section » ET pour « Configurer ». */
function ChampsSection({ s, poser, desactive, prefixe }) {
    return (
        <>
            <div className="form-group">
                <label htmlFor={`${prefixe}-titre`}>Titre de la section <span style={{ color: '#dc2626' }}>*</span></label>
                <input id={`${prefixe}-titre`} className="input-sm" value={s.titre} disabled={desactive}
                       placeholder="Ex : Contexte du projet" onChange={(e) => poser('titre', e.target.value)} />
            </div>
            <div className="fmf-deux">
                <div className="form-group">
                    <label htmlFor={`${prefixe}-type`}>Type de champ</label>
                    <select id={`${prefixe}-type`} className="input-sm" value={s.type_champ} disabled={desactive}
                            onChange={(e) => poser('type_champ', e.target.value)}>
                        {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor={`${prefixe}-oblig`}>Saisie</label>
                    <select id={`${prefixe}-oblig`} className="input-sm" value={s.obligatoire ? '1' : '0'} disabled={desactive}
                            onChange={(e) => poser('obligatoire', e.target.value === '1')}>
                        <option value="1">Obligatoire</option>
                        <option value="0">Optionnelle</option>
                    </select>
                </div>
            </div>
            <div className="form-group">
                <label htmlFor={`${prefixe}-qui`}>Qui remplit ?</label>
                <select id={`${prefixe}-qui`} className="input-sm" value={s.remplissage} disabled={desactive}
                        onChange={(e) => poser('remplissage', e.target.value)}>
                    {QUI.map((q) => <option key={q.v} value={q.v}>{q.l}</option>)}
                </select>
            </div>
            {AVEC_OPTIONS.includes(s.type_champ) && (
                <div className="form-group">
                    <label htmlFor={`${prefixe}-options`}>
                        Options <span style={{ fontWeight: 400 }}>(une par ligne{s.type_champ === 'checkbox' ? ' — vide : une seule case « Oui »' : ', au moins deux'})</span>
                    </label>
                    <textarea id={`${prefixe}-options`} className="input-sm" style={{ minHeight: 64 }} value={s.optionsTexte} disabled={desactive}
                              placeholder={'Industrie\nServices\nCommerce'} onChange={(e) => poser('optionsTexte', e.target.value)} />
                </div>
            )}
            <div className="form-group">
                <label htmlFor={`${prefixe}-aide`}>Texte d'aide (placeholder)</label>
                <input id={`${prefixe}-aide`} className="input-sm" value={s.placeholder || ''} disabled={desactive}
                       placeholder="Texte indicatif affiché dans le champ…" onChange={(e) => poser('placeholder', e.target.value)} />
            </div>
        </>
    );
}

const NOUVELLE = { titre: '', type_champ: 'textarea', obligatoire: true, remplissage: 'maitre', placeholder: '', optionsTexte: '' };

function StageEditeurFiche({ modele, modifiable, enCours, erreur, onEnregistrer, onAnnuler, onApercu }) {
    const [nom, setNom] = useState(modele.nom || '');
    const [intro, setIntro] = useState(modele.texte_intro || '');
    // E175 (22/10) : « on demande toujours une mission » — un modèle NEUF part avec Missions activée.
    const [missions, setMissions] = useState(modele.id ? modele.afficher_missions === true : true);
    const [missionsLibelle, setMissionsLibelle] = useState(modele.missions_libelle || '');
    const [missionsAide, setMissionsAide] = useState(modele.missions_placeholder || '');
    const [sections, setSections] = useState(() => {
        // Un modèle NEUF part avec la case 🏛️ ; un modèle existant garde ce qu'il a.
        const s = modele.sections?.length ? modele.sections : (modele.id ? [] : [SECTION_CONVENTION]);
        return s.map((x) => ({ ...x, optionsTexte: versTexte(x.options), _cle: cle() }));
    });
    const [configure, setConfigure] = useState(null);     // _cle de la section ouverte, ou 'missions'
    const [nouvelle, setNouvelle] = useState(null);
    const [survol, setSurvol] = useState(null);
    const glisse = useRef(null);
    const lectureSeule = !modifiable;
    const figees = (modele.utilise_dans || []).filter((u) => u.figee);

    const poser = (k, champ, v) => setSections((xs) => xs.map((x) => (x._cle === k ? { ...x, [champ]: v } : x)));
    const retirer = (k) => setSections((xs) => xs.filter((x) => x._cle !== k));
    const deplacer = (depart, cible) => setSections((xs) => {
        if (depart === cible || cible < 0 || cible >= xs.length) return xs;
        const copie = [...xs]; const [x] = copie.splice(depart, 1); copie.splice(cible, 0, x); return copie;
    });
    const ajouter = () => {
        const s = { ...nouvelle, id: null, actif: true, _cle: cle() };
        setSections((xs) => [...xs, s]);
        setNouvelle(null);
    };

    const charge = () => ({
        nom, texte_intro: intro, afficher_missions: missions, missions_libelle: missionsLibelle, missions_placeholder: missionsAide,
        sections: sections.map((x) => ({
            id: x.id, code: x.code || null, titre: x.titre, type_champ: x.type_champ, remplissage: x.remplissage,
            obligatoire: x.obligatoire === true, placeholder: x.placeholder || '',
            options: AVEC_OPTIONS.includes(x.type_champ) ? versListe(x.optionsTexte) : [],
            actif: x.actif !== false
        }))
    });

    const actives = sections.filter((x) => x.actif !== false);
    const sansConvention = !sections.some((x) => x.code === CODE_CONVENTION);
    const remettreConvention = () => setSections((xs) => [...xs, { ...SECTION_CONVENTION, optionsTexte: '', _cle: cle() }]);

    return (
        <div className="modele-editor">
            <div className="modele-editor-header">
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--stg-vert-d)' }}>
                    <i className="fa-solid fa-pen" /> {modele.id ? `Édition — ${modele.nom}` : 'Nouveau modèle de fiche de liaison'}
                </div>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}><i className="fa-solid fa-xmark" /> Fermer</button>
            </div>

            {figees.length > 0 && (
                <div className="stg-encart stg-encart-alerte">
                    <i className="fa-solid fa-lock" />
                    <span>
                        Ce modèle est utilisé par <b>{figees.length} campagne{figees.length > 1 ? 's' : ''} déjà lancée{figees.length > 1 ? 's' : ''}</b>
                        {' '}({figees.map((u) => u.libelle).join(', ')}) : leurs étudiants et maîtres de stage remplissent la fiche de leur premier envoi.
                        Votre modification vaudra pour les copies de ce processus (Dupliquer, Récupérer N-1).
                    </span>
                </div>
            )}

            <div className="stg-encart stg-encart-info">
                <i className="fa-solid fa-circle-info" />
                <div>
                    <strong>Structure fixe (non modifiable) :</strong> étudiant pré-rempli · informations entreprise · coordonnées maître de stage.<br />
                    <span style={{ opacity: 0.8 }}>Ce sont des données obligatoires collectées dans toutes les fiches. Seul le contenu ci-dessous est configurable.</span>
                </div>
            </div>

            <div className="fmf-grille">
                {/* ── Colonne gauche : le contenu configurable ── */}
                <div>
                    <div className="modele-sous-titre">Contenu configurable</div>
                    <div className="form-group" style={{ marginBottom: 10 }}>
                        <label htmlFor="fmf-nom">Nom du modèle <span style={{ color: '#dc2626' }}>*</span></label>
                        <input id="fmf-nom" className="input-sm" value={nom} disabled={lectureSeule} onChange={(e) => setNom(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="fmf-intro">Texte d'introduction <span style={{ fontWeight: 400 }}>(affiché en haut du formulaire)</span></label>
                        <textarea id="fmf-intro" className="input-sm" style={{ minHeight: 90 }} value={intro} disabled={lectureSeule}
                                  onChange={(e) => setIntro(e.target.value)} />
                        <div style={{ fontSize: 10, color: 'var(--stg-muted)', marginTop: 3 }}>
                            <i className="fa-solid fa-lightbulb" /> Ce texte contextualise le stage pour l'étudiant et le maître de stage qui reçoivent le lien.
                        </div>
                    </div>
                </div>

                {/* ── Colonne droite : les sections optionnelles ── */}
                <div>
                    <div className="fmf-entete-colonne">
                        <div className="modele-sous-titre" style={{ marginBottom: 0 }}>Sections optionnelles</div>
                        <button type="button" className="btn btn-green btn-sm" disabled={lectureSeule || !!nouvelle}
                                onClick={() => { setNouvelle({ ...NOUVELLE }); setConfigure(null); }}>
                            <i className="fa-solid fa-plus" /> Ajouter une section
                        </button>
                    </div>
                    {sansConvention && !lectureSeule && (
                        <div className="fmf-resume" style={{ margin: '0 0 8px' }}>
                            <i className="fa-solid fa-building-columns" /> Pas de case « convention de l'université » dans ce modèle (la colonne 🏛️ du tableau de bord dira « — »).{' '}
                            <button type="button" className="stg-lien-bouton" onClick={remettreConvention}>Remettre la case</button>
                        </div>
                    )}

                    {/* Missions : intégrée de base, place fixe (avant les sections libres). */}
                    <div className={'fmf-section' + (missions ? '' : ' inactive')}>
                        <div className="fmf-ligne">
                            <div style={{ flex: 1 }}>
                                <div className="fmf-titre">Missions proposées</div>
                                <div className="fmf-resume">Texte long · rempli par l'étudiant, corrigé par le maître de stage</div>
                            </div>
                            <button type="button" className={'toggle' + (missions ? ' on' : '')} disabled={lectureSeule}
                                    aria-pressed={missions} aria-label="Afficher la section Missions" title="Afficher / masquer la section Missions"
                                    onClick={() => { setMissions(!missions); if (!missions) setConfigure('missions'); }} />
                            <button type="button" className="action-btn fmf-petit-btn" title="Configurer"
                                    aria-label="Configurer la section Missions" aria-expanded={configure === 'missions'}
                                    onClick={() => setConfigure(configure === 'missions' ? null : 'missions')}>
                                <i className="fa-solid fa-sliders" />
                            </button>
                        </div>
                        {configure === 'missions' && (
                            <div className="fmf-detail">
                                <div className="form-group">
                                    <label htmlFor="fmf-mis-lib">Libellé affiché</label>
                                    <input id="fmf-mis-lib" className="input-sm" value={missionsLibelle} disabled={lectureSeule}
                                           placeholder="Décrivez les principales missions que l'étudiant réalisera"
                                           onChange={(e) => setMissionsLibelle(e.target.value)} />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label htmlFor="fmf-mis-aide">Texte d'aide</label>
                                    <input id="fmf-mis-aide" className="input-sm" value={missionsAide} disabled={lectureSeule}
                                           placeholder="Ex : Participation aux audits internes, mise à jour des procédures…"
                                           onChange={(e) => setMissionsAide(e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>

                    {sections.map((s, i) => (
                        <div key={s._cle} className={'fmf-section' + (s.actif === false ? ' inactive' : '') + (survol === i ? ' survol' : '')}
                             onDragOver={(e) => { e.preventDefault(); setSurvol(i); }}
                             onDragLeave={() => setSurvol(null)}
                             onDrop={(e) => { e.preventDefault(); setSurvol(null); if (glisse.current !== null) deplacer(glisse.current, i); glisse.current = null; }}>
                            <div className="fmf-ligne">
                                {(
                                    <button type="button" className="critere-drag" draggable={!lectureSeule} disabled={lectureSeule}
                                            title="Glisser pour réordonner (ou flèches haut / bas)" aria-label={`Déplacer la section ${s.titre || i + 1}`}
                                            onDragStart={(e) => { glisse.current = i; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                                            onDragEnd={() => setSurvol(null)}
                                            onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); deplacer(i, i - 1); } if (e.key === 'ArrowDown') { e.preventDefault(); deplacer(i, i + 1); } }}>
                                        <i className="fa-solid fa-grip-vertical" />
                                    </button>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="fmf-titre">{s.code === CODE_CONVENTION && '🏛️ '}{s.titre || <i style={{ color: 'var(--stg-muted)' }}>(section sans titre)</i>}</div>
                                    <div className="fmf-resume">
                                        {resume(s)}{s.code === CODE_CONVENTION ? ' · lue par la colonne 🏛️ du tableau de bord' : ''}
                                    </div>
                                </div>
                                {s.utilise && <span className="modeles-usage-chip" title="Des réponses existent : elle ne se supprime pas, elle se désactive">déjà répondue</span>}
                                <button type="button" className={'toggle' + (s.actif !== false ? ' on' : '')}
                                        disabled={lectureSeule} aria-pressed={s.actif !== false}
                                        aria-label={`Section active — ${s.titre}`}
                                        title={s.actif !== false ? 'Désactiver : elle quittera le formulaire des prochaines campagnes' : 'Réactiver cette section'}
                                        onClick={() => poser(s._cle, 'actif', s.actif === false)} />
                                {(
                                    <button type="button" className="action-btn fmf-petit-btn" title="Configurer"
                                            aria-label={`Configurer la section ${s.titre}`} aria-expanded={configure === s._cle}
                                            onClick={() => setConfigure(configure === s._cle ? null : s._cle)}>
                                        <i className="fa-solid fa-sliders" />
                                    </button>
                                )}
                                {!s.utilise && (
                                    <button type="button" className="action-btn fmf-petit-btn" title="Supprimer cette section" disabled={lectureSeule}
                                            aria-label={`Supprimer la section ${s.titre}`} onClick={() => retirer(s._cle)}>
                                        <i className="fa-solid fa-trash" style={{ color: '#dc2626' }} />
                                    </button>
                                )}
                            </div>
                            {configure === s._cle && (
                                <div className="fmf-detail">
                                    <ChampsSection s={s} prefixe={`fmf-${s._cle}`} desactive={lectureSeule} poser={(champ, v) => poser(s._cle, champ, v)} />
                                </div>
                            )}
                        </div>
                    ))}

                    {nouvelle && (
                        <div className="fmf-nouvelle">
                            <div className="fmf-nouvelle-titre"><i className="fa-solid fa-plus" /> Nouvelle section</div>
                            <ChampsSection s={nouvelle} prefixe="fmf-ns" desactive={lectureSeule} poser={(champ, v) => setNouvelle((n) => ({ ...n, [champ]: v }))} />
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" className="btn btn-green btn-sm" disabled={!nouvelle.titre.trim()} onClick={ajouter}>
                                    <i className="fa-solid fa-check" /> Ajouter
                                </button>
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => setNouvelle(null)}>Annuler</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Le bandeau : l'ordre réel du formulaire envoyé ── */}
            <div className="fmf-ordre">
                <div className="fmf-ordre-titre"><i className="fa-solid fa-eye" /> Ordre des sections dans le formulaire envoyé</div>
                <div className="fmf-ordre-chips">
                    {['① Étudiant', '② Entreprise', '③ Maître de stage'].map((x) => (
                        <React.Fragment key={x}>
                            <span className="fmf-chip fixe" title="Structure fixe">{x}</span>
                            <i className="fa-solid fa-arrow-right fmf-ordre-fleche" />
                        </React.Fragment>
                    ))}
                    {missions && <><span className="fmf-chip config">Missions</span><i className="fa-solid fa-arrow-right fmf-ordre-fleche" /></>}
                    {actives.map((x) => (
                        <React.Fragment key={x._cle}>
                            <span className="fmf-chip config" title={resume(x)}>{x.titre || '(sans titre)'}</span>
                            <i className="fa-solid fa-arrow-right fmf-ordre-fleche" />
                        </React.Fragment>
                    ))}
                </div>
                <div className="fmf-ordre-legende">
                    <i className="fa-solid fa-lock" style={{ fontSize: 9 }} /> Grisé = structure fixe &nbsp;·&nbsp;
                    <i className="fa-solid fa-gear" style={{ fontSize: 9, color: '#92400e' }} /> Coloré = configuré dans ce modèle
                </div>
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur" style={{ marginTop: 12 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="modele-actions">
                <button type="button" className="btn btn-green btn-sm" disabled={lectureSeule || enCours} onClick={() => onEnregistrer(charge())}>
                    {enCours ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Enregistrement…</> : <><i className="fa-solid fa-check" /> Enregistrer</>}
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={onAnnuler}>Annuler</button>
                <button type="button" className="lien-apercu" onClick={() => onApercu(charge())}><i className="fa-solid fa-eye" /> Voir l'aperçu formulaire complet</button>
            </div>
            {lectureSeule && <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 6 }}><i className="fa-solid fa-circle-info" /> Lecture seule : votre profil ne permet pas de modifier ce module.</div>}
        </div>
    );
}

export default StageEditeurFiche;
